import React from 'react';
import PropTypes from 'prop-types';

import { useGetMatchesQuery } from 'common/store/modules/api/afl-api';
import { getTranslation } from 'common/react/utils/translations';

import { MatchBar } from './MatchBar';

const PAGE_SIZE = 50;

/**
 * Fetches the round(s) the template asked for and hands the matches to the match
 * bar, which groups them by round.
 *
 * @param {object} props - Props.
 * @param {object} props.data - The container's `data-*` attributes (see
 * fixture-bar.ftl) — all strings. `roundNumbers` is a comma-separated list, which
 * the API takes as-is; the template decides what's in it.
 * @returns {React.ReactElement} The fixture bar.
 */
export default function FixtureBar({ data }) {
    // Key order matters - the Cypress intercept matches on the built query string.
    // Skipping on a blank list matters too: the API ignores an empty `roundNumber`
    // and answers with the whole season.
    const { data: matches, isLoading } = useGetMatchesQuery(
        {
            competitionId: data.comp,
            compSeasonId: data.compseason,
            roundNumber: data.roundNumbers,
            pageSize: PAGE_SIZE
        },
        { skip: !data.roundNumbers }
    );

    const hasMatches = Boolean(matches?.length);

    return (
        <div className="fixture-bar__container afl-design-system flex w-full min-h-4xl items-center bg-surface l:pl-l my-xs">
            {isLoading && (
                <div className="fixture-bar__is-loading flex w-full items-center justify-center"></div>
            )}
            {!isLoading && hasMatches && (
                <MatchBar
                    matches={matches}
                    allMatchesLink={data.allMatchesLink}
                    isFinalsMode={data.finalsMode === 'true'}
                />
            )}
            {!isLoading && !hasMatches && (
                <div className="fixture-bar__is-error flex w-full items-center justify-center">
                    {getTranslation('label.fixtureBar.noMatchesMessage')}
                </div>
            )}
        </div>
    );
}

FixtureBar.propTypes = {
    data: PropTypes.object.isRequired
};
